/**
 * IndexedDB wrapper for offline module caching.
 *
 * Stores:
 *   - modules:  { code (key), data (full module JSON), size_bytes, downloaded_at, audio_urls }
 *   - blobs:    { url (key), blob, size, mime }
 *   - meta:     { key, value }  — app-level (e.g. last_sync)
 *
 * All blob keys are the FULL URL the app would normally fetch — this avoids
 * duplicating hash logic that already exists server-side for TTS.
 */
const DB_NAME = "qf_offline_v1";
const DB_VERSION = 1;
const STORE_MODULES = "modules";
const STORE_BLOBS = "blobs";
const STORE_META = "meta";

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not supported in this browser"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_MODULES)) {
        db.createObjectStore(STORE_MODULES, { keyPath: "code" });
      }
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: "url" });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(name, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, mode);
    let result;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("aborted"));
    Promise.resolve(fn(tx.objectStore(name)))
      .then((r) => { result = r; })
      .catch((e) => { try { tx.abort(); } catch (_e) { /* harmless */ } reject(e); });
  });
}

// -------- modules store --------
export async function putModule(record) {
  return withStore(STORE_MODULES, "readwrite", (s) => promisify(s.put(record)));
}
export async function getModule(code) {
  return withStore(STORE_MODULES, "readonly", (s) => promisify(s.get(code)));
}
export async function deleteModule(code) {
  return withStore(STORE_MODULES, "readwrite", (s) => promisify(s.delete(code)));
}
export async function listModules() {
  return withStore(STORE_MODULES, "readonly", (s) => promisify(s.getAll()));
}

// -------- blobs store --------
export async function putBlob(record) {
  return withStore(STORE_BLOBS, "readwrite", (s) => promisify(s.put(record)));
}
export async function getBlob(url) {
  return withStore(STORE_BLOBS, "readonly", (s) => promisify(s.get(url)));
}
export async function deleteBlob(url) {
  return withStore(STORE_BLOBS, "readwrite", (s) => promisify(s.delete(url)));
}
export async function listAllBlobUrls() {
  return withStore(STORE_BLOBS, "readonly", (s) => promisify(s.getAllKeys()));
}

// -------- meta store --------
export async function setMeta(key, value) {
  return withStore(STORE_META, "readwrite", (s) => promisify(s.put({ key, value })));
}
export async function getMeta(key) {
  const r = await withStore(STORE_META, "readonly", (s) => promisify(s.get(key)));
  return r?.value;
}
